// Back from Yoco (TAS-463): Yoco sends the player to
// <origin>/?order=TST-12345&pay=success|cancelled|failed, which is the join
// screen's address. Mounted once in App.jsx around every route: when the URL
// is a return from Yoco, this shows how the payment went INSTEAD of the route
// (so the join screen never mounts under it), and the routes come back once
// the player leaves it.
//
// The query is read once and taken off the address, but the return itself is
// kept in sessionStorage until the player leaves the screen, so a refresh while
// confirming (or after a cancel) lands back on it with its status, retry and
// back-to-game link. What the URL says is only
// which way the player left Yoco's page; whether the order is paid comes from
// the server (PaymentReturnScreen, usePaymentReturn).
//
// This file stays small because App.jsx loads it eagerly; the screen itself
// is its own chunk, fetched only on a return.
import { Suspense, useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { readPaymentReturn, readPendingPayment } from '@tasted/commerce/cardPayment';
import { lazyWithRetry as lazy } from '../../utils/lazyRetry';
import { currentSessionCode, playerStorage } from '../../utils/playerStorage';

const PaymentReturnScreen = lazy(() => import('./PaymentReturnScreen'));

const KEY = 'tasted:payment-return';

const sameCode = (a, b) => String(a).trim().toUpperCase() === String(b).trim().toUpperCase();

/**
 * Whether a kept return belongs on this page (Codex review round 2, P1). It
 * is kept with the game and player its order was paid from, and comes back
 * only in that same game for that same player, or on the address the return
 * screen leaves behind (`/`, no game), which is what a refresh lands on. Any
 * other page, such as another game opened in this tab, is left alone, and
 * the return stays kept for its own game.
 */
function belongsHere(saved) {
  const code = currentSessionCode();
  if (code) {
    if (!saved.gameCode || !sameCode(saved.gameCode, code)) return false;
  } else if (window.location.pathname !== '/') return false;
  const player = playerStorage.get(code || saved.gameCode);
  return !saved.playerId || !player || saved.playerId === player;
}

/** The return to show, and whether it is kept in sessionStorage (a refresh can
 *  restore it from there). When storage throws, only the URL still has it. */
function loadReturn() {
  if (typeof window === 'undefined') return { ret: null, persisted: false };
  const fromUrl = readPaymentReturn();
  try {
    if (fromUrl) {
      // The game and player the payment was started from, as useCardPayment
      // kept them with the order.
      const extra = readPendingPayment(fromUrl.orderNo)?.extra;
      const entry = {
        ...fromUrl,
        ...(typeof extra?.gameCode === 'string' && extra.gameCode ? { gameCode: extra.gameCode } : {}),
        ...(typeof extra?.playerId === 'string' && extra.playerId ? { playerId: extra.playerId } : {}),
      };
      window.sessionStorage.setItem(KEY, JSON.stringify(entry));
      return { ret: fromUrl, persisted: true };
    }
    const saved = JSON.parse(window.sessionStorage.getItem(KEY) || 'null');
    const ok = saved && typeof saved.orderNo === 'string' && typeof saved.result === 'string' && belongsHere(saved);
    return { ret: ok ? { orderNo: saved.orderNo, result: saved.result } : null, persisted: true };
  } catch {
    return { ret: fromUrl, persisted: false };
  }
}

function forgetReturn() {
  // The query may still be on the address (storage threw): leaving clears it too.
  window.history.replaceState(window.history.state, '', cleanUrl());
  try { window.sessionStorage.removeItem(KEY); } catch { /* storage blocked: nothing was kept */ }
}

/** The current address without ?order and ?pay. */
function cleanUrl() {
  const url = new URL(window.location.href);
  url.searchParams.delete('order');
  url.searchParams.delete('pay');
  return `${url.pathname}${url.search}${url.hash}`;
}

/**
 * @param {object} props
 * @param {import('react').ReactNode} props.children  the routes, shown when this is not a return from Yoco
 * @param {{tries?: number, every?: number}} [props.poll]  how long to wait for Yoco's webhook (tests shorten it)
 */
export default function PaymentReturn({ children, poll }) {
  const [loaded] = useState(loadReturn);
  const [ret, setRet] = useState(loaded.ret);
  const navigate = useNavigate();

  // Off the address straight away. The router's own state is kept, so its
  // history index stays right; the router catches up when the player leaves.
  // Only once the return is kept in storage: if storage threw, the address is
  // all a refresh has, so the query stays until the player leaves the screen.
  useEffect(() => {
    if (!ret || !loaded.persisted) return;
    window.history.replaceState(window.history.state, '', cleanUrl());
  }, [ret, loaded.persisted]);

  const close = useCallback(() => {
    forgetReturn();
    setRet(null);
    navigate(cleanUrl(), { replace: true });
  }, [navigate]);

  const backToGame = useCallback((gameCode) => {
    forgetReturn();
    setRet(null);
    navigate(`/game/thank-you?code=${encodeURIComponent(gameCode)}`, { replace: true });
  }, [navigate]);

  if (!ret) return children;
  return (
    <Suspense fallback={<div className="min-h-[100dvh] bg-[#1C3B29]" />}>
      <PaymentReturnScreen ret={ret} poll={poll} onClose={close} onBackToGame={backToGame} />
    </Suspense>
  );
}
